import * as React from 'react'
import handleViewport from 'react-in-viewport'

import { ProductBlock as GravityProductBlock } from '@thg-commerce/gravity-patterns'
import { ProductBlockProps as GravityProductBlockProps } from '@thg-commerce/gravity-patterns/ProductBlock/ProductBlock'

import { ProductPriceProps } from '@thg-commerce/gravity-elements'
import {
  useSiteConfig,
  useSiteDefinition,
  useSessionSettings,
  useTheme,
} from '@thg-commerce/enterprise-core'
import { QubitBadgeRenderer } from '@thg-commerce/enterprise-components/Qubit/QubitBadge/QubitBadgeRenderer'
import { PricePerUnit } from '@thg-commerce/enterprise-components/PricePerUnit'
import { PricePerUnit as PricePerUnitType } from '@thg-commerce/enterprise-network/src/transformers/product/pricePerUnit'
import { ProductClearance } from '@thg-commerce/enterprise-components/ProductClearance'
import {
  FulfilmentMethodIconsRendererProps,
  FulfilmentMethodIconsRenderer,
} from '@thg-commerce/enterprise-components/FulfilmentMethods/FulfilmentMethodIconsRenderer'
import {
  CallbackSetEvent,
  QubitViewType,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Qubit'
import { Beacon } from '@thg-commerce/enterprise-network/src/transformers/sponsoredAds/products'
import {
  ProductContentItem,
  ProductContentStringListValue,
  ProductContentStringValue,
  ProductContentValue,
  FulfilmentMethod,
  InStockLocation,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  BreakpointArray,
  Orientation,
  ZIndexLevel,
} from '@thg-commerce/gravity-theme'
import { QubitBadgeStyle } from '@thg-commerce/enterprise-widget-product-list-page/theme'
import { pushToEnhancedDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'

import { useQuickBuyClickHandler } from './hooks'
import { PowerReviewCategorySnippet } from '../../../PowerReviewCategorySnippet'
import { getPowerReviewGroups } from '../../../src/PowerReview/PowerReview'

export interface ContentItem extends Omit<ProductContentItem, 'value'> {
  value:
    | ProductContentValue
    | {
        __typename: 'ProductContentStringListValue'
        stringListValue: ProductContentStringListValue['value']
      }
    | {
        __typename: 'ProductContentStringValue'
        stringValue: ProductContentStringValue['value']
      }
}

export type ProductBlockProps = GravityProductBlockProps &
  ProductPrice & {
    sku: string
    button?: Omit<GravityProductBlockProps['button'], 'onClick'> & {
      quickBuyTitle: string
      productInStock?: boolean
    }
    emitImpressionEvent?: (params: {
      logger: any
      eventType: CallbackSetEvent
      callbackData?: string
      callbackURL?: string
    }) => void
    fromRecommendations?: boolean
    onQuickBuyClickEventEmitter?: () => void
    fulfilmentMethodIconsProps?: FulfilmentMethodIconsRendererProps
    externalIdentifier?: string
    powerReviewGroups?: string
    qubitBadgeStyle?: QubitBadgeStyle
    qubitBadgeZIndex?: ZIndexLevel
    displayQuickBuyButton?: Boolean
    label?: string
    eligibleForFulfilmentMethods?: FulfilmentMethod
    weightGroups?: string[]
    content?: ContentItem[]
    clearanceTag?: string
    inStockLocations?: InStockLocation[]
    leadTime?: number
    isBookable?: boolean
    loopSlides?: boolean
    sponsoredAdsBeacons?: {
      onBasketChangeBeacon?: Beacon
      onClickBeacon?: Beacon
      onViewBeacon?: Beacon
    }
    hideGap?: boolean
    enableQuickbuyButtonOnDesktop?: boolean
    eligibleForNextDayDelivery?: boolean
    eligibleForHomeDelivery?: boolean
    eligibleForDatedDelivery?: boolean
    outOfGauge?: boolean
    storeDeliveryEnabled?: boolean
    pricePerUnit?: PricePerUnitType
    wasPrice?: string
  }

interface ProductPrice {
  price?: ProductPriceProps
}

export const ProductBlock = (props: ProductBlockProps) => {
  return <EnterpriseProductBlock {...props} />
}

const SponsoredAdsFireInViewElement = (props: {
  inViewport: boolean
  enterCount: number
  onViewBeacon: Beacon
  forwardedRef: React.RefObject<HTMLDivElement>
}) => {
  const shouldFireBeacons =
    props.inViewport && props.enterCount && props.enterCount < 2

  if (props.onViewBeacon && shouldFireBeacons) {
    navigator.sendBeacon(props.onViewBeacon.url)
  }
  return <div style={{ position: 'absolute' }} ref={props.forwardedRef}></div>
}

const SponsoredAdsFireInViewportElement = handleViewport(
  SponsoredAdsFireInViewElement,
)

const EnterpriseProductBlock = React.memo((props: ProductBlockProps) => {
  const { displayQuickBuyButton = true } = props
  const { defaultLocale, domain } = useSiteDefinition()
  const { quickBuyTitle: quickBuyButtonTitle, productInStock } = {
    ...props.button,
  }
  const quickBuyClickHandler = useQuickBuyClickHandler(
    props.sku,
    props.fromRecommendations,
    props.sponsoredAdsBeacons?.onBasketChangeBeacon,
    `https://${domain}${props.url}`,
  )
  const {
    showProductBlockBrandTitle,
    showProductBlockBrandLogo,
    hideProductListSwatch,
    previewQuickbuy,
    enableSmallQuickBuyButton,
    hideProductListProductBlockButton,
    enableQuickbuyOnlyOnMobile,
    showPowerReview,
    useGA4EnhancedEcom,
    inlineQuickBuyButton,
    enablePricePerUnit,
    showFlagsOnProduct,
    addLabelsToFulfilmentIcons,
    enableSponsoredAds,
    alternateProductKeysForSwatches,
    contentValueToApplyAlternateSwatch,
    productContentItemWasPriceTypeKey,
  } = useSiteConfig()

  const theme = useTheme()

  const sessionSettings = useSessionSettings()
  const quickBuyEnabled =
    previewQuickbuy && productInStock && displayQuickBuyButton

  const buttonClickHandler = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.stopPropagation()
    quickBuyClickHandler(event)
    props.onQuickBuyClickEventEmitter?.()
  }

  const QubitBadgeAnotation = ({ children }) => {
    const orientation: BreakpointArray<Orientation> = [
      Orientation.VERTICAL,
      Orientation.VERTICAL,
      Orientation.HORIZONTAL,
      Orientation.HORIZONTAL,
    ]

    return (
      <div id="placeholder" style={{ position: 'relative' }}>
        <QubitBadgeRenderer
          emitImpressionEvent={props.emitImpressionEvent}
          productId={props.sku.toString()}
          type={QubitViewType.CATEGORY}
          orientation={orientation}
          badgeStyle={props.qubitBadgeStyle}
          zIndex={props.qubitBadgeZIndex}
        />
        {children}
      </div>
    )
  }

  const PowerReviewCategorySnippetComponent = props.externalIdentifier ? (
    <PowerReviewCategorySnippet
      sku={parseInt(props.sku, 10)}
      externalId={parseInt(props.externalIdentifier, 10)}
      locale={defaultLocale}
      pathname={props.url}
      id={props.sku}
      powerReviewGroups={getPowerReviewGroups(props.content)}
      powerReviewsAlignment={props.attributes?.powerReviewsAlignment}
    />
  ) : null

  const hbg_wasPrice = props?.content?.find(
    (contentItem) => contentItem.key === productContentItemWasPriceTypeKey,
  )

  const hasWasPrice = !!hbg_wasPrice

  const wasPriceAvailable =
    hbg_wasPrice?.value.__typename === 'ProductContentStringValue' &&
    'stringValue' in hbg_wasPrice.value
      ? hbg_wasPrice.value.stringValue
      : undefined

  return (
    <GravityProductBlock
      {...props}
      attributes={{
        ...props.attributes,
        gap: props.hideGap ? [0] : undefined,
      }}
      image={props.image}
      label={props.label}
      showBrandTitle={showProductBlockBrandTitle}
      showBrandLogo={showProductBlockBrandLogo}
      hideProductListSwatch={hideProductListSwatch}
      annotateImageComponent={
        props.qubitBadgeStyle ? QubitBadgeAnotation : undefined
      }
      enableSmallQuickBuyButton={
        enableSmallQuickBuyButton && !props.enableQuickbuyButtonOnDesktop
      }
      inlineQuickBuyButton={inlineQuickBuyButton}
      alternateProductKeysForSwatches={alternateProductKeysForSwatches}
      contentValueToApplyAlternateSwatch={contentValueToApplyAlternateSwatch}
      onClickEventEmitter={() => {
        useGA4EnhancedEcom &&
          pushToEnhancedDataLayer({
            event: 'ecom_event',
            event_name: 'select_item',
            ecommerce: {
              currencyCode: sessionSettings?.currency,
              items: [
                {
                  item_name: props.title?.value,
                  item_id: props.sku,
                  price: props.price?.price?.defaultPrice,
                  item_brand: props.brand?.name,
                },
              ],
            },
          })

        if (enableSponsoredAds && props.sponsoredAdsBeacons?.onClickBeacon) {
          navigator.sendBeacon(props.sponsoredAdsBeacons.onClickBeacon.url)
        }
      }}
      showPowerReview={showPowerReview}
      powerReviewCategorySnippetComponent={PowerReviewCategorySnippetComponent}
      externalIdentifier={props.externalIdentifier}
      button={
        !hideProductListProductBlockButton && props.button
          ? {
              ...props.button,
              enableQuickbuyOnlyOnMobile:
                enableQuickbuyOnlyOnMobile &&
                !props.enableQuickbuyButtonOnDesktop,
              title:
                quickBuyEnabled && quickBuyButtonTitle
                  ? quickBuyButtonTitle
                  : props.button.title,
              onClick: quickBuyEnabled ? buttonClickHandler : undefined,
            }
          : undefined
      }
      icons={
        props.fulfilmentMethodIconsProps?.hasClickAndCollect ? (
          <FulfilmentMethodIconsRenderer
            isCheckStock={props.fulfilmentMethodIconsProps.isCheckStock}
            canProductBeStockedChecked={
              props.fulfilmentMethodIconsProps.canProductBeStockedChecked
            }
            deliveryFulfilmentTextStyle={
              props.fulfilmentMethodIconsProps.deliveryFulfilmentTextStyle
            }
            hasClickAndCollect={
              props.fulfilmentMethodIconsProps.hasClickAndCollect
            }
            i18nText={props.fulfilmentMethodIconsProps.i18nText}
            iconsAvailability={
              props.fulfilmentMethodIconsProps.iconsAvailability
            }
            eligibleForNextDayDelivery={
              props.fulfilmentMethodIconsProps.eligibleForNextDayDelivery
            }
            eligibleForHomeDelivery={
              props.fulfilmentMethodIconsProps.eligibleForHomeDelivery
            }
            eligibleForDatedDelivery={
              props.fulfilmentMethodIconsProps.eligibleForDatedDelivery
            }
            weightGroups={props.fulfilmentMethodIconsProps.weightGroups}
            outOfGauge={props.fulfilmentMethodIconsProps.outOfGauge}
            storeDeliveryEnabled={
              props.fulfilmentMethodIconsProps.storeDeliveryEnabled
            }
            isOrderInStore={props.fulfilmentMethodIconsProps.isOrderInStore}
            addLabelsToIcons={addLabelsToFulfilmentIcons}
          />
        ) : undefined
      }
      secondWasPriceAvailable={Boolean(props.wasPrice) || hasWasPrice}
      secondWasPriceValue={props.wasPrice || wasPriceAvailable}
      pricePerUnitContent={
        enablePricePerUnit && props?.pricePerUnit?.displayValue ? (
          <PricePerUnit
            pricePerUnit={props.pricePerUnit}
            font={
              !props.fromRecommendations
                ? theme.patterns.productBlock.components.pricePerUnit?.font
                : undefined
            }
          />
        ) : undefined
      }
      content={
        <React.Fragment>
          {showFlagsOnProduct ? (
            <ProductClearance
              order={theme.patterns.productBlock.components.clearanceTag?.order}
              content={props?.content}
              clearanceTag={props?.clearanceTag}
            />
          ) : undefined}
          {enableSponsoredAds ? (
            <SponsoredAdsFireInViewportElement
              onViewBeacon={props.sponsoredAdsBeacons?.onViewBeacon}
            />
          ) : undefined}
        </React.Fragment>
      }
      addLabelsToIcons={addLabelsToFulfilmentIcons}
      loopSlides={props.loopSlides}
    />
  )
})
